import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import { testIds } from "@/testIds";

const T = testIds.sharing.removeDialog;

export interface RemoveChoice {
  notify: boolean;
  allowReply: boolean;
}

/**
 * Remove someone's access (Olivia, 10 Oct; the user: no notice by default, a
 * lab may be removing someone unwanted). Two boxes, both unticked every time:
 * "Tell … by email and in Lilia", and only then "Let them reply to me". The
 * line under them says what will happen.
 */
export function RemoveAccessDialog({
  open,
  name,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  name: string;
  onCancel: () => void;
  onConfirm: (choice: RemoveChoice) => void;
}) {
  const [notify, setNotify] = useState(false);
  const [allowReply, setAllowReply] = useState(false);
  // Never remembered: every removal starts unticked.
  useEffect(() => {
    if (open) {
      setNotify(false);
      setAllowReply(false);
    }
  }, [open]);
  const { t } = useTranslation("sharing");
  const first = name.split(/\s+/)[0] || name;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid={T.root} className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t("remove.title", { name })}</DialogTitle>
          <DialogDescription>{t("remove.description", { first })}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 text-[13.5px]">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              data-testid={T.notify}
              checked={notify}
              onChange={(e) => {
                setNotify(e.target.checked);
                if (!e.target.checked) setAllowReply(false);
              }}
            />
            {t("remove.notify", { first })}
          </label>
          <label className={"flex items-center gap-2 " + (notify ? "" : "opacity-50")}>
            <input type="checkbox" data-testid={T.allowReply} checked={allowReply} disabled={!notify} onChange={(e) => setAllowReply(e.target.checked)} />
            {t("remove.allowReply")}
          </label>
          <p data-testid={T.summary} className="mt-1 text-[12.5px] text-[var(--lilia-text-muted)]">
            {notify
              ? t(allowReply ? "remove.willGetReply" : "remove.willGet", { first })
              : t("remove.notNotified", { first })}
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" data-testid={T.cancel} onClick={onCancel}>
            {t("remove.cancel")}
          </Button>
          <Button
            data-testid={T.confirm}
            className="!bg-[var(--lilia-danger,#c62828)] !text-white hover:!opacity-90"
            onClick={() => onConfirm({ notify, allowReply: notify && allowReply })}
          >
            {t("remove.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
